/**
 * PeriodIndicatorsPanel — Indicadores POR PERÍODO, en solo lectura.
 *
 * Responde dos preguntas que antes no tenían respuesta en la aplicación:
 *
 *  1. ¿Con qué se calculó este mes? Para un período ya calculado los valores salen
 *     de su snapshot, así que **no cambian** si alguien corrige un indicador
 *     después. Un mes cerrado muestra lo que efectivamente se usó.
 *  2. ¿Cuándo voy a poder calcular el mes que viene? Cada indicador pendiente
 *     dice de qué organismo depende y en qué fecha se espera.
 *
 * Nota de theming: no existen tokens semánticos para estados (éxito / advertencia /
 * histórico), así que los tres estados usan paletas Tailwind **siempre con variante
 * `dark:`** — el mismo criterio que el resto del módulo. Cuando el rediseño defina
 * tokens de estado (deuda #234), esto se reemplaza en un solo lugar: `STATE_STYLES`.
 */
import { useState } from 'react'
import { DataTable } from '@/components/ds/DataTable'
import {
  AlertTriangle, CalendarClock, CheckCircle2, ExternalLink, History, Info, Lock,
} from 'lucide-react'
import { useIndicatorPeriods, type IndicatorPeriod, type PeriodIndicator } from '../hooks/usePayroll'
import { PAYROLL_PERIOD_STATUS } from '../../../components/ds/statusCatalogs'

const MONTHS = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio',
  'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre']

const STATE_STYLES: Record<PeriodIndicator['state'], { chip: string; label: string }> = {
  used: {
    chip: 'bg-glass-3 text-content-muted',
    label: 'Usado en el cálculo',
  },
  available: {
    chip: 'bg-success-surface text-success-content',
    label: 'Disponible',
  },
  pending: {
    chip: 'bg-warning-surface text-warning-content',
    label: 'Pendiente de publicación',
  },
}


const num = (v: string | number, dec = 0) =>
  new Intl.NumberFormat('es-CL', { minimumFractionDigits: dec, maximumFractionDigits: dec })
    .format(Number(v))

/** Cada indicador se lee en su propia unidad: pesos, UF o porcentaje. */
function formatValue(e: PeriodIndicator): string {
  if (e.value === null || e.value === '') return '—'
  switch (e.key) {
    case 'uf':
      return `$${num(e.value, 2)}`
    case 'utm':
    case 'imm':
      return `$${num(e.value)}`
    case 'topes_imponibles':
      return `${num(e.value, 2)} UF`
    case 'sis':
      return `${num(Number(e.value) * 100, 2)}%`
    default:
      return num(e.value, 2)
  }
}

function fecha(iso: string | null): string {
  if (!iso) return ''
  const [y, m, d] = iso.split('-').map(Number)
  return `${d} de ${MONTHS[m - 1].toLowerCase()} de ${y}`
}

function StateChip({ state }: { state: PeriodIndicator['state'] }) {
  const s = STATE_STYLES[state]
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${s.chip}`}>
      {s.label}
    </span>
  )
}

/** Los indicadores que son una tabla (IUSC, asignación familiar) o un grupo de
 *  valores (topes, reforma) se despliegan aparte del valor único. */
function IndicatorDetail({ e }: { e: PeriodIndicator }) {
  if (e.key === 'tabla_iusc' && e.tramos?.length) {
    return (
      <div className="mt-3 overflow-x-auto">
        <p className="text-xs text-content-muted mb-2">
          Convertida a pesos con la UTM del período{e.utm_base ? ` ($${num(e.utm_base)})` : ''}.
          Así se compara directamente con la tabla que publica el SII.
        </p>
        <DataTable<NonNullable<PeriodIndicator['tramos']>[number]>
          columns={[
            { key: 'from_clp', header: 'Desde', numeric: true,
              render: t => <span className="text-content">${num(t.from_clp || 0)}</span> },
            { key: 'to_clp', header: 'Hasta', numeric: true,
              render: t => (
                <span className="text-content-muted">{t.to_clp ? `$${num(t.to_clp)}` : 'y más'}</span>
              ) },
            { key: 'factor', header: 'Factor', numeric: true,
              render: t => (
                <span className="text-content-muted">
                  {Number(t.factor) === 0 ? 'Exento' : `${num(Number(t.factor) * 100, 1)}%`}
                </span>
              ) },
            { key: 'rebate_clp', header: 'Rebaja', numeric: true,
              render: t => <span className="text-content-muted">${num(t.rebate_clp || 0, 2)}</span> },
          ]}
          rows={e.tramos}
          rowKey={(_, i) => i}
          minWidth={480}
          surface={false}
          caption="Escala del impuesto único de segunda categoría, en pesos del período"
        />
      </div>
    )
  }

  if (e.key === 'asignacion_familiar' && e.tramos?.length) {
    return (
      <DataTable<NonNullable<PeriodIndicator['tramos']>[number]>
        columns={[
          { key: 'tramo', header: 'Tramo',
            render: t => <span className="text-content font-medium">Tramo {t.tramo}</span> },
          { key: 'income_from', header: 'Renta desde', numeric: true,
            render: t => <span className="text-content-muted">${num(t.income_from || 0)}</span> },
          { key: 'income_to', header: 'Renta hasta', numeric: true,
            render: t => (
              <span className="text-content-muted">{t.income_to ? `$${num(t.income_to)}` : 'Sin tope'}</span>
            ) },
          { key: 'amount', header: 'Monto por carga', numeric: true,
            render: t => <span className="text-content">${num(t.amount || 0)}</span> },
        ]}
        rows={e.tramos}
        rowKey={(_, i) => i}
        minWidth={416}
        surface={false}
        className="mt-3"
        caption="Tramos de asignación familiar vigentes en el período"
      />
    )
  }

  if (!e.detalle) return null
  // ⚠️ Toda clave que llegue en `detalle` necesita etiqueta: sin ella se muestra
  // la clave cruda (`crp_rate`), que es lo que pasaba con los componentes de la
  // segunda etapa de la reforma.
  const etiquetas: Record<string, string> = {
    tope_afp_uf: 'Tope AFP', tope_salud_uf: 'Tope salud', tope_cesantia_uf: 'Tope cesantía',
    valid_from: 'Vigente desde',
    // Reforma de pensiones (Ley 21.735). El aporte del empleador va primero
    // porque es la cifra que se compara; debajo, de qué se compone.
    aporte_empleador_rate: 'Aporte del empleador',
    individual_rate: 'Cuenta individual',
    crp_rate: 'Rentabilidad protegida',
    ssp_rate: 'Expectativa de vida',
    sis_rate: 'SIS',
    // No es "Aporte total": el SIS queda fuera de este campo.
    total_rate: 'Subtotal reforma',
  }
  const filas = Object.entries(e.detalle).filter(([, v]) => v !== null && v !== '')
  if (!filas.length) return null
  return (
    <dl className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-1.5 text-xs">
      {filas.map(([k, v]) => (
        <div key={k} className="flex justify-between gap-2">
          <dt className={k === 'aporte_empleador_rate'
            ? 'text-content font-medium' : 'text-content-muted'}>
            {etiquetas[k] || k}
          </dt>
          <dd className={`text-content tabular-nums${
            k === 'aporte_empleador_rate' ? ' font-medium' : ''}`}>
            {k === 'valid_from' ? v
              : k.endsWith('_uf') ? `${num(v!, 2)} UF`
              : k.endsWith('_rate') ? `${num(Number(v) * 100, 2)}%`
              : v}
          </dd>
        </div>
      ))}
    </dl>
  )
}

function IndicatorRow({ e }: { e: PeriodIndicator }) {
  const tieneDetalle = !!(e.tramos?.length || e.detalle)
  return (
    <div className="px-4 py-3">
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <div className="min-w-0">
          <p className="text-sm font-medium text-content">{e.label}</p>
          <p className="text-xs text-content-subtle">
            {e.organismo}
            {e.source_url && (
              <a href={e.source_url} target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-0.5 ml-1.5 text-primary hover:underline">
                fuente <ExternalLink size={10} />
              </a>
            )}
          </p>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          {e.value !== null && (
            <span className="text-sm font-semibold text-content tabular-nums">{formatValue(e)}</span>
          )}
          <StateChip state={e.state} />
        </div>
      </div>

      {e.state === 'pending' && (
        <p className="mt-1.5 text-xs text-warning-content flex items-center gap-1.5">
          <CalendarClock size={12} />
          {e.expected_date
            ? <>Se espera el {fecha(e.expected_date)}
                {e.confidence === 'medium' && ' (fecha estimada, el organismo no la publica)'}</>
            : <>Sin fecha prevista: cambia cuando la autoridad lo resuelve</>}
        </p>
      )}

      {e.differs_from_legal && (
        <p className="mt-1.5 text-xs text-warning-content flex items-start gap-1.5">
          <AlertTriangle size={12} className="mt-0.5 shrink-0" />
          <span>
            Este período se calculó con <strong>{formatValue(e)}</strong>, y la norma vigente para
            el mes indica <strong>{formatValue({ ...e, value: e.legal_value })}</strong>. El valor
            usado no se modifica: las liquidaciones ya emitidas quedan como están.
          </span>
        </p>
      )}

      {tieneDetalle && <IndicatorDetail e={e} />}
    </div>
  )
}

function PeriodCard({ p }: { p: IndicatorPeriod }) {
  const faltantes = p.indicators.filter(e => e.state === 'pending')

  return (
    <div className="bg-glass backdrop-blur-glass rounded-card border border-divider overflow-hidden shadow-card">
      <div className="px-4 py-3 border-b border-divider flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-bold text-content">
            {MONTHS[p.month - 1]} {p.year}
          </h3>
          {p.period_status && (
            <span className="text-xs text-content-muted">
              · {PAYROLL_PERIOD_STATUS[p.period_status]?.label ?? p.period_status}
            </span>
          )}
          {p.is_closed && (
            <span className="inline-flex items-center gap-1 text-xs text-content-subtle">
              <Lock size={11} /> histórico
            </span>
          )}
        </div>
        {p.is_closed ? (
          <span className="inline-flex items-center gap-1.5 text-xs text-content-muted">
            <History size={12} /> Valores del cálculo, no los actuales
          </span>
        ) : p.ready_to_calculate ? (
          <span className="inline-flex items-center gap-1.5 text-xs text-success-content">
            <CheckCircle2 size={12} /> Listo para calcular
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 text-xs text-warning-content">
            <AlertTriangle size={12} /> Faltan {faltantes.length} indicador{faltantes.length !== 1 ? 'es' : ''}
          </span>
        )}
      </div>

      {!p.is_closed && faltantes.length > 0 && (
        <div className="px-4 py-3 bg-warning-surface border-b border-divider">
          <p className="text-xs font-medium text-warning-content-strong flex items-center gap-1.5">
            <AlertTriangle size={13} /> Este período todavía no tiene todos los indicadores
          </p>
          <ul className="mt-1.5 space-y-0.5 text-xs text-warning-content-strong">
            {faltantes.map(e => (
              <li key={e.key}>
                · <strong>{e.label}</strong>
                {e.expected_date ? ` — se espera el ${fecha(e.expected_date)}` : ' — sin fecha prevista'}
              </li>
            ))}
          </ul>
          <p className="mt-2 text-xs text-amber-800/80 dark:text-amber-300/70">
            Se puede calcular igual, pero los conceptos que dependen de estos valores quedarían
            con el último dato conocido. Conviene esperar la publicación.
          </p>
        </div>
      )}

      {p.differs_from_legal.length > 0 && (
        <div className="px-4 py-3 bg-warning-surface border-b border-divider">
          <p className="text-xs text-warning-content-strong flex items-start gap-1.5">
            <AlertTriangle size={13} className="mt-0.5 shrink-0" />
            <span>
              Este período se calculó con <strong>{p.differs_from_legal.length}</strong>{' '}
              {p.differs_from_legal.length === 1 ? 'indicador' : 'indicadores'} distintos de la
              norma vigente para el mes. Está señalado en cada uno más abajo.
            </span>
          </p>
        </div>
      )}

      <div className="divide-y divide-divider">
        {p.indicators.map(e => <IndicatorRow key={e.key} e={e} />)}
      </div>

      <div className="px-4 py-3 border-t border-divider bg-surface-muted">
        <p className="text-xs font-semibold text-content-muted mb-1.5">
          Topes del beneficio tributario
        </p>
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-xs">
          <div className="flex justify-between gap-2">
            <dt className="text-content-muted">
              APV con rebaja de impuesto ({p.caps.apv_monthly_uf} UF al mes)
            </dt>
            <dd className="text-content tabular-nums">
              {p.caps.apv_monthly_clp ? `$${num(p.caps.apv_monthly_clp)}` : '—'}
            </dd>
          </div>
          <div className="flex justify-between gap-2">
            <dt className="text-content-muted">
              Depósito convenido ({p.caps.deposito_convenido_annual_uf} UF al año)
            </dt>
            <dd className="text-content tabular-nums">
              {p.caps.deposito_convenido_annual_clp
                ? `$${num(p.caps.deposito_convenido_annual_clp)}` : '—'}
            </dd>
          </div>
        </dl>
      </div>
    </div>
  )
}

export function PeriodIndicatorsPanel() {
  const { data, isLoading } = useIndicatorPeriods()
  const [abierto, setAbierto] = useState<string | null>(null)

  const periods = data?.periods ?? []
  // Por defecto se abre el primero con algo que resolver; si no, el más reciente.
  const clave = (p: IndicatorPeriod) => `${p.year}-${p.month}`
  const porDefecto = periods.find(p => !p.is_closed && !p.ready_to_calculate) ?? periods[0]
  const activo = abierto ?? (porDefecto ? clave(porDefecto) : null)

  if (isLoading) {
    return <div className="text-center py-12 text-content-subtle text-sm">Cargando indicadores...</div>
  }
  if (!periods.length) {
    return <div className="text-center py-12 text-content-subtle text-sm">Sin períodos para mostrar.</div>
  }

  const seleccionado = periods.find(p => clave(p) === activo) ?? periods[0]

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-2 text-xs text-content-muted bg-glass-3 rounded-lg px-3 py-2.5">
        <Info size={14} className="mt-0.5 shrink-0" />
        <p>
          Los indicadores los mantiene Clobi y son los mismos para todas las empresas del país,
          así que no se editan desde acá. Un período <strong>ya calculado</strong> muestra los
          valores con los que se calculó: si un indicador se corrige después, los meses anteriores
          no cambian.
        </p>
      </div>

      <div className="flex gap-1.5 overflow-x-auto pb-1">
        {periods.map(p => {
          const k = clave(p)
          const sel = k === activo
          const alerta = !p.is_closed && !p.ready_to_calculate
          return (
            <button key={k} onClick={() => setAbierto(k)}
              className={`shrink-0 px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors
                ${sel
                  ? 'bg-primary text-on-primary border-primary'
                  : 'bg-surface text-content-muted border-divider hover:bg-soft'}`}>
              {MONTHS[p.month - 1].slice(0, 3)} {String(p.year).slice(2)}
              {alerta && (
                <span className={`ml-1.5 ${sel ? 'text-white/90' : 'text-warning-content-soft'}`}>
                  ●
                </span>
              )}
            </button>
          )
        })}
      </div>

      <PeriodCard p={seleccionado} />
    </div>
  )
}
